import { useEffect, useRef } from "react";
import {
  AutocompleteInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  useGetOne,
} from "react-admin";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useFormContext, useWatch } from "react-hook-form";
import { NeedSadaratFields } from "@/components/raza/NeedSadaratFields";
import { RazaEventItsPickerInput } from "@/components/raza/RazaEventItsPickerInput";
import RazaEventITSLookup from "@/components/raza/RazaEventITSLookup";
import { RazaEventVenueAddressField } from "@/components/raza/RazaEventVenueAddressField";
import { MAJLIS_START_TIME_CHOICES } from "@/utils/raza-event-time";
import {
  razaEventCalendarBaseTitle,
  razaEventLabelFromTitle,
} from "@/utils/raza-backed-event-calendar-title";
import { purposeHelperText } from "@/utils/purpose-helper-text";

/** Keeps `title` aligned with calendar `eventLabel` (purpose name or "Raza event"); shows `eventLabel - host` preview. */
function RazaBackedEventTitleInput() {
  const { setValue } = useFormContext();
  const purpose = useWatch({ name: "purpose" });
  const hostItsNo = useWatch({ name: "hostItsNo" });
  const hostName = useWatch({ name: "hostName" });
  const title = useWatch({ name: "title" });

  const purposeId = typeof purpose === "string" ? purpose.trim() : "";
  const { data: purposeRow } = useGetOne(
    "bookingPurpose",
    { id: purposeId },
    { enabled: purposeId.length > 0 }
  );
  const purposeName = typeof purposeRow?.name === "string" ? purposeRow.name.trim() : "";

  const autoEventLabel = razaEventLabelFromTitle("", purposeName);
  const lastSuggested = useRef<string | null>(null);

  useEffect(() => {
    const cur = typeof title === "string" ? title.trim() : "";
    if (!cur || cur === lastSuggested.current) {
      if (autoEventLabel !== cur) {
        setValue("title", autoEventLabel, { shouldDirty: true, shouldValidate: false });
      }
      lastSuggested.current = autoEventLabel;
    }
  }, [autoEventLabel, title, setValue]);

  const preview = razaEventCalendarBaseTitle(title, hostName, hostItsNo, purposeName);

  return (
    <TextInput
      source="title"
      label="Title"
      fullWidth
      helperText={`Google Calendar summary: ${preview}. Until raza is granted, a [RAZA PENDING] prefix is added.`}
    />
  );
}

/** Form fields for raza-backed events (linked to a `RazaRequest`), used from the raza request detail UI. */
export function RazaBackedEventFormFields() {
  return (
    <Grid container spacing={2}>
      <Grid
        sx={{
          pr: { md: 2 },
          borderRight: { md: "1px solid" },
          borderColor: "divider",
        }}
        size={{ xs: 12, md: 6 }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Event & contacts
        </Typography>
        <ReferenceInput source="purpose" reference="bookingPurpose" perPage={100}>
          <AutocompleteInput
            label="Purpose"
            fullWidth
            helperText={purposeHelperText("razaEvent")}
          />
        </ReferenceInput>
        <RazaEventITSLookup source="hostItsNo" label="Host ITS" />
        <TextInput
          source="hostName"
          label="Host name"
          fullWidth
          helperText="Defaults from directory or raza request when ITS matches; otherwise enter manually"
        />
        <NeedSadaratFields />
        <RazaEventItsPickerInput
          source="zakereenItsNo"
          label="Zakereen (optional)"
          helperText="Search by name or ITS; must exist in itsdata"
        />
        <DateInput source="date" label="Event date" fullWidth isRequired />
        <SelectInput
          source="startTime"
          choices={MAJLIS_START_TIME_CHOICES}
          label="Time"
          fullWidth
          isRequired
          helperText="15-minute steps"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Venue & notes
        </Typography>
        <RazaEventVenueAddressField />
        <TextInput source="mobileNo" label="Contact mobile" fullWidth />
        <RazaBackedEventTitleInput />
        <TextInput source="notes" label="Notes" fullWidth multiline minRows={4} />
      </Grid>
    </Grid>
  );
}
